import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { CardContent } from '../ui/card';
import { Save, CreditCard, Loader2, ExternalLink } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

export default function PlatformPaymentSettings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [form, setForm] = useState({
    enabled: false,
    mode: 'test',
    key_id: '',
    key_secret: '',
    webhook_secret: '',
    key_secret_masked: '',
    webhook_secret_masked: '',
    is_configured: false,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('platform/payment-settings')
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data || {};
        setWebhookUrl(d.webhook_url || '');
        setForm({
          enabled: !!d.enabled,
          mode: d.mode || 'test',
          key_id: d.key_id || '',
          key_secret: '',
          webhook_secret: '',
          key_secret_masked: d.key_secret_masked || '',
          webhook_secret_masked: d.webhook_secret_masked || '',
          is_configured: !!d.is_configured,
        });
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: 'Error',
            description: 'Failed to load payment settings',
            variant: 'destructive',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        mode: form.mode,
        key_id: form.key_id.trim(),
        key_secret: form.key_secret.trim() || form.key_secret_masked,
        webhook_secret: form.webhook_secret.trim() || form.webhook_secret_masked,
      };
      const res = await api.post('platform/payment-settings', payload);
      const d = res.data?.data || {};
      setWebhookUrl(d.webhook_url || webhookUrl);
      setForm((prev) => ({
        ...prev,
        enabled: !!d.enabled,
        mode: d.mode || 'test',
        key_id: d.key_id || prev.key_id,
        key_secret: '',
        webhook_secret: '',
        key_secret_masked: d.key_secret_masked || '',
        webhook_secret_masked: d.webhook_secret_masked || '',
        is_configured: !!d.is_configured,
      }));
      toast({ title: 'Saved', description: 'Razorpay payment settings updated' });
    } catch (e) {
      toast({
        title: 'Error',
        description: e.response?.data?.message || 'Failed to save payment settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <CardContent className="p-6 space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 p-4 text-sm">
        <p className="font-medium text-blue-900 dark:text-blue-100 flex items-center gap-2">
          <CreditCard className="h-4 w-4" /> Razorpay (India)
        </p>
        <p className="text-muted-foreground mt-1">
          School subscriptions are collected via Razorpay. Get your API keys from the
          {' '}
          <a
            href="https://dashboard.razorpay.com/app/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            Razorpay Dashboard <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label>Enable Razorpay payments</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Schools can subscribe only when this is enabled and keys are valid.
          </p>
        </div>
        <Switch
          checked={form.enabled}
          onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
        />
      </div>

      <div>
        <Label>Mode</Label>
        <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
          <SelectTrigger className="mt-1.5 rounded-lg max-w-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="test">Test</SelectItem>
            <SelectItem value="live">Live</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Use Test keys for development; switch to Live for production.
        </p>
      </div>

      <div>
        <Label>Key ID</Label>
        <Input
          value={form.key_id}
          onChange={(e) => setForm({ ...form, key_id: e.target.value })}
          className="mt-1.5 rounded-lg font-mono text-sm"
          placeholder="rzp_test_xxxxxxxx or rzp_live_xxxxxxxx"
        />
      </div>

      <div>
        <Label>Key Secret</Label>
        <Input
          type="password"
          value={form.key_secret}
          onChange={(e) => setForm({ ...form, key_secret: e.target.value })}
          className="mt-1.5 rounded-lg font-mono text-sm"
          placeholder={form.key_secret_masked || 'Enter key secret'}
        />
        {form.key_secret_masked && !form.key_secret && (
          <p className="text-xs text-muted-foreground mt-1">
            Saved: {form.key_secret_masked}. Leave blank to keep unchanged.
          </p>
        )}
      </div>

      <div>
        <Label>Webhook Secret</Label>
        <Input
          type="password"
          value={form.webhook_secret}
          onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
          className="mt-1.5 rounded-lg font-mono text-sm"
          placeholder={form.webhook_secret_masked || 'Enter webhook secret'}
        />
        {form.webhook_secret_masked && !form.webhook_secret && (
          <p className="text-xs text-muted-foreground mt-1">
            Saved: {form.webhook_secret_masked}. Leave blank to keep unchanged.
          </p>
        )}
      </div>

      {webhookUrl && (
        <div>
          <Label>Webhook URL</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Add this URL in Razorpay Dashboard → Webhooks. Subscribe to payment.captured and payment.failed.
          </p>
          <Input readOnly value={webhookUrl} className="rounded-lg font-mono text-xs" />
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} className={PRIMARY_BTN} disabled={saving}>
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save payment settings'}
        </Button>
        {form.is_configured && (
          <span className="text-sm text-green-600">Configured</span>
        )}
      </div>
    </CardContent>
  );
}
